import React from 'react'
import EnhancedMeta from '../components/meta/enhanced-meta'
import Layout from '../components/layout/layout'
import { graphql } from 'gatsby'
import Parallax from '../components/parallax'
import ServiceTable from '../components/service-table'
import GoogleMapsEmbed from '../components/google-maps-embed'
import { Treatment, Question } from '../interfeces'
import RelatedTreatments from '../components/related-treatments'
import { generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../utils/schema-generator'

interface Props {
  data: any
  location: Location
}

const treatment: Treatment = {
  duration: '60 minutes',
  session: '1 session',
  results: 'Results 6-8 weeks',
  cost: 'From £45',
  downtime: 'None',
}

const relatedTreatments = [
  {
    name: 'Microblading & Nanoblading Cheshunt',
    slug: 'microblading',
    description:
      'Explore semi-permanent brow options for long-lasting definition beyond lamination.',
  },
  {
    name: 'Lash Lift & Tint Cheshunt',
    slug: 'lash-lift-and-tint',
    description:
      'Frame your eyes with naturally curled, darkened lashes that last 6–8 weeks.',
  },
  {
    name: 'Dermaplaning Facial Cheshunt',
    slug: 'dermaplaning',
    description:
      'Smooth away peach fuzz and dead skin for an ultra-clean canvas before brow treatments.',
  },
]

const questions: Array<Question> = [
  {
    question: 'How much does brow lamination and tint cost in Cheshunt?',
    answer:
      'Brow lamination and tint at Ada Aesthetics in Cheshunt starts from £45 for the standard treatment. We also offer HD Brows for £65, which includes detailed brow mapping, shaping, tinting, and lamination. Book a free consultation at our Cheshunt clinic to discuss which option is best for your brow goals.',
  },
  {
    question: 'How does Brow Lamination work?',
    answer:
      'The technician applies a brow straightening cream to soften and tame straggly brows into a neat shape. The brows are then brushed upwards to give the illusion of fullness, and a setting lotion is applied to keep them in shape. Lasts 6-8 weeks.',
  },
  {
    question: 'Brow Lamination vs Microblading',
    answer:
      'Laminated brows are just as impressive as microbladed brows, but the two treatments are completely different. Microblading is much more invasive because it is a semi-permanent tattoo involving needles. Tattooed brows give the illusion of fullness, whereas laminating straightens out unruly brows for a smooth, defined shape. Although laminated brows are temporary, they are a much more affordable way to get perfectly groomed brows.',
  },
  {
    question: 'How long does brow lamination last?',
    answer:
      'Brow lamination at Ada Aesthetics in Cheshunt typically lasts 6-8 weeks. The longevity depends on your hair growth cycle and aftercare. We recommend avoiding water and products on your brows for 24 hours after treatment for best results.',
  },
  {
    question: 'Is brow lamination safe?',
    answer:
      'Yes! When performed by trained professionals like our team at Ada Aesthetics in Cheshunt, brow lamination is very safe. We use professional-grade products designed specifically for brow treatments. However, it is not suitable for pregnant women or those with very sensitive skin.',
  },
]

const Eyebrows: React.FC<Props> = ({ data, location }: Props) => {
  const meta = data.site?.meta

  const serviceSchema = generateServiceSchema(
    'Brow Lamination and Tint in Cheshunt',
    'Professional brow lamination and tinting in Cheshunt, Hertfordshire. Transform unruly brows into perfectly groomed, fuller-looking eyebrows. Non-invasive alternative to microblading.',
    '45'
  )

  const faqSchema = generateFAQSchema(questions.map(q => ({
    question: q.question,
    answer: q.answer
  })))

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://adaaesthetics.uk' },
    { name: 'Treatments', url: 'https://adaaesthetics.uk/treatments' },
    { name: 'Brow Lamination Cheshunt', url: 'https://adaaesthetics.uk/eyebrows-lamination-and-tint' }
  ])

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema, breadcrumbSchema]
  }

  return (
    <Layout location={location}>
      <EnhancedMeta
        title="Brow Lamination & Tint Cheshunt - HD Brows | Ada Aesthetics"
        description="Professional brow lamination and tinting in Cheshunt from £45. Transform unruly brows into perfectly groomed eyebrows. HD Brows available. Book: 07478705252"
        keywords="brow lamination Cheshunt, eyebrow tint, HD brows Hertfordshire, brow lift Cheshunt, eyebrow lamination"
        url="https://adaaesthetics.uk/eyebrows-lamination-and-tint"
        schemaData={combinedSchema}
        site={meta}
      />
      <Parallax titleName="Brow Lamination and Tint" customClass="page_title" />

      <section className="ls service-single s-pt-70 s-pb-20 s-pb-sm-50 s-py-lg-100 s-pt-xl-150 s-pb-xl-100 c-gutter-60 c-mb-50">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 className="special-heading">Transform Unruly Brows into Perfectly Groomed Eyebrows with Brow Lamination in Cheshunt</h1>
              <p className="excerpt">
                Patchy brows, gaps from overplucking, or hairs that never sit quite right? Brow lamination and tint at
                Ada Aesthetics in Cheshunt smooth, straighten and tint each hair into place so your brows look fuller,
                more defined and perfectly groomed from the moment you wake up—without needles. From £45, with HD Brows
                available for a fully mapped, bespoke brow shape.
              </p>

              <h2>What is Brow Lamination?</h2>
              <p>
                Women in Russia and the Far East have been straightening their
                brows for years. Think of it as a lash lift for your brows:
                expect fuller, more defined brows in under an hour without
                needles or pain. The technician applies a brow straightening cream to soften and tame straggly brows into a neat shape. The brows are then brushed upwards to give the illusion of fullness, and a setting lotion is applied to keep them in shape. Lasts 6-8 weeks.
              </p>
              <p>
                At Ada Aesthetics in Cheshunt, we understand the power of expertly
                groomed brows and specialise in coaxing unruly brows into shape.
                Our brow lamination creates perfectly defined, fuller-looking
                eyebrows that frame your face beautifully.
              </p>

              <h2>Brow Lamination vs Microblading</h2>
              <p>
                Laminated brows are just as impressive as microbladed brows, but the two treatments are completely different. Microblading is much more invasive because it is a semi-permanent tattoo involving needles. Tattooed brows give the illusion of fullness, whereas laminating straightens out unruly brows for a smooth, defined shape. Although laminated brows are temporary, they are a much more affordable way to get perfectly groomed brows without the commitment of semi-permanent makeup.
              </p>

              <h2>What is HD Brows?</h2>
              <p>
                At Ada Aesthetics in Cheshunt, we also offer HD Brows for £65, which includes detailed brow mapping, shaping, tinting, and lamination. This comprehensive treatment creates the perfect brow shape tailored to your face, ensuring symmetry and definition.
              </p>

              <h2>Benefits of Brow Lamination</h2>
              <ul>
                <li><strong>Transforms unruly brows</strong> into perfectly groomed shape</li>
                <li><strong>Creates illusion of fullness</strong> - brushes brows upwards</li>
                <li><strong>No needles or pain</strong> - completely non-invasive</li>
                <li><strong>Affordable alternative</strong> to microblading</li>
                <li><strong>Long-lasting results</strong> - 6-8 weeks</li>
                <li><strong>Quick treatment</strong> - under an hour</li>
                <li><strong>Low maintenance</strong> - wake up with perfect brows</li>
              </ul>

              <h2>Is Brow Lamination Safe?</h2>
              <p>
                Yes! When performed by trained professionals like our team at Ada Aesthetics in Cheshunt, brow lamination is very safe. We use professional-grade products designed specifically for brow treatments. However, it is not suitable for pregnant women or those with very sensitive skin.
              </p>

              <h2>Why Choose Ada Aesthetics for Brow Lamination in Cheshunt?</h2>
              <ul>
                <li><strong>Expert practitioners</strong> trained in brow lamination techniques</li>
                <li><strong>Two treatment options</strong> - standard and HD Brows</li>
                <li><strong>Natural results</strong> - enhances your own brows beautifully</li>
                <li><strong>Competitive pricing</strong> from £45</li>
                <li><strong>Conveniently located</strong> in Cheshunt, Hertfordshire</li>
                <li><strong>Serving local areas</strong> including Waltham Cross, Broxbourne, Hoddesdon</li>
              </ul>

              <h2>Book Your Brow Lamination Treatment in Cheshunt</h2>
              <p>
                Ready for perfectly groomed, defined brows? Join clients who trust us for professional brow lamination treatments.
                Schedule your appointment today - our experienced team will help you choose between standard brow lamination and HD Brows to achieve your perfect brow aesthetic.
              </p>
              <p>
                <strong>Book today to secure your appointment</strong> - Call us at <strong>07478705252</strong> or reserve your treatment online.
              </p>

              <RelatedTreatments treatments={relatedTreatments} />

              <GoogleMapsEmbed
                embedUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2469.9642841486544!2d-0.04449842346989817!3d51.70251797183743!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761f5e6c8f7d0d%3A0x9f3e8f3e8f3e8f3e!2s15%20Ousden%20Drive%2C%20Cheshunt%2C%20Waltham%20Cross%20EN8%209RL!5e0!3m2!1sen!2suk!4v1234567890123"
                height={450}
                title="Ada Aesthetics Brow Lamination Clinic Location in Cheshunt"
                loading="lazy"
              />
            </div>
          </div>
          <ServiceTable treatment={treatment} questions={questions} />
        </div>
      </section>
    </Layout>
  )
}

export default Eyebrows

export const pageQuery = graphql`
  query Eyebrows {
    site {
      meta: siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`
